import styled from "@emotion/styled";
import * as React from "react";
import { Field } from "react-final-form";

import { LockBlack } from "@livechat/design-system-icons";
import { DesignToken, Icon, SpacingToken, Tag, Text } from "@livechat/design-system-react-components";

import { RadioOptionFinalForm } from "components/form";
import Role from "constants/role";
import { withVar } from "themes/helpers";

import { RoleChangePermissions } from "../constants";

import { AgentFormSection } from "./AgentFormSection";
import { AgentFormSectionId } from "./constants";

interface IProps {
  agentRole: Role;
  changePermissions: RoleChangePermissions;
}

const PermissionManagement: React.FC<IProps> = ({ agentRole, changePermissions }) => {
  const isAdminDisabled = changePermissions !== RoleChangePermissions.CanChange && agentRole !== Role.Owner;

  const isAgentDisabled = changePermissions !== RoleChangePermissions.CanChange && agentRole !== Role.Normal;

  const isViewerDisabled = changePermissions === RoleChangePermissions.None && agentRole !== Role.Viewer;

  const shouldShowLock =
    changePermissions === RoleChangePermissions.None ||
    (changePermissions === RoleChangePermissions.CanChangeToViewerOnly && agentRole === Role.Viewer);

  return (
    <AgentFormSection
      header={{
        id: AgentFormSectionId.PermissionsManagement,
        component: (
          <>
            Roles and permissions
            {shouldShowLock && (
              <LockIcon source={LockBlack} size="small" customColor={withVar(DesignToken.ContentBasicSecondary)} />
            )}
          </>
        ),
      }}
    >
      <>
        <Field
          component={RadioOptionFinalForm}
          name="permission"
          value={Role.Owner}
          type="radio"
          data-testid="admin-role"
          disabled={isAdminDisabled}
        >
          <Text>Admin</Text>
          <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)}>
            Full access to all features. Can invite agents and manage subscription, payments, and all ticket-related
            actions.
          </Text>
        </Field>
        <Field
          component={RadioOptionFinalForm}
          name="permission"
          value={Role.Normal}
          type="radio"
          data-testid="agent-role"
          disabled={isAgentDisabled}
        >
          <Text>Agent</Text>
          <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)}>
            Access to personal settings and essential ticket-related actions.
          </Text>
        </Field>
        <Field
          component={RadioOptionFinalForm}
          name="permission"
          value={Role.Viewer}
          type="radio"
          data-testid="viewer-role"
          disabled={isViewerDisabled}
        >
          <Text>
            Viewer <ViewerTag size="small">Free of charge</ViewerTag>
          </Text>
          <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)}>
            Can read and browse tickets, but can&lsquo;t take any actions.
          </Text>
        </Field>
      </>
    </AgentFormSection>
  );
};

export default PermissionManagement;

const LockIcon = styled(Icon)`
  margin-left: var(${SpacingToken.Spacing2});
`;

const ViewerTag = styled(Tag)`
  margin-left: var(${SpacingToken.Spacing1});
`;
